import { Flex, Form, Row } from '@/components';
import { useAnalysisPageSearchQueryStates } from '@/features/analysis/_hooks/search-query-states';
import { useCreateQueryStore } from '@/features/search/_hooks/query-store';
import type { AnalysisSearchFormType } from '@/features/search/_models/analysis-search';
import { processAnalysisCascaderComparison } from '@/features/search/_utils/analysis-cascader-comparison';
import { convertInputAnalysisValueToRequestParams } from '@/features/search/_utils/project-amount-search';
import { validateProjectNumber } from '@/features/search/_utils/projects-search';
import { searchQueryKeyMap } from '@/features/search/_utils/query-keys';
import { useInvalidateQuery, useIsFetching } from '@/hooks';
import { type FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCurrentFiscalYear } from '../../_hooks/get-current-fiscal-year';
import { useProjectsSearchStore } from '../../_stores/projects-search-store';
import { AccountingTypeColField } from '../_common/fields/AccountingTypeColField';
import { AccountingTypeIdsColField } from '../_common/fields/AccountingTypeIdsColField';
import { CategoriesColField } from '../_common/fields/CategoriesColField';
import { ImplementationMethodsColField } from '../_common/fields/ImplementationMethodsColField';
import { KeywordColField } from '../_common/fields/KeywordColField';
import { NestedOrganizationIdsColField } from '../_common/fields/NestedOrganizationIdsColField';
import { NestedPolicyIdsColField } from '../_common/fields/NestedPolicyIdsColField';
import { PaymentColField } from '../_common/fields/PaymentColField';
import { ProjectNumbersColField } from '../_common/fields/ProjectNumbersColField';
import { AdditionalSearch } from './AdditionalSearch';
import { SearchButton } from './SearchButton';
import { AggregationTargetColField } from './fields/AggregationTargetColField';
import { AggregationTypeColField } from './fields/AggregationTypeColField';
import { AnalysisAxisColField } from './fields/AnalysisAxisColField';
import { AnalysisTargetColField } from './fields/AnalysisTargetColField';
import { FiscalYearRangeColField } from './fields/FiscalYearRangeColField';
import { AggregationTags } from './tags/AggregationTags';
import { AnalysisSearchSearchFieldTags } from './tags/AnalysisSearchSearchFieldTags';

const FORM_ID = 'analysis-search';

export const SearchForm: FC<{
  onResetPayment: () => void;
  resetCount: number;
}> = ({ onResetPayment, resetCount }) => {
  const { t } = useTranslation();
  const { invalidate } = useInvalidateQuery();
  const { mutateAsync: createQueryStore } = useCreateQueryStore();
  const { setSearchQueryStates, searchQueryStates } =
    useAnalysisPageSearchQueryStates();
  const isFetching = useIsFetching({
    queryKey: searchQueryKeyMap.projectAnalysis(),
  });

  const currentFiscalYear = useCurrentFiscalYear();
  const { cascaderTargetIds: currentCascaderValues } = useProjectsSearchStore();
  const {
    handleSubmit,
    formState: { isDirty },
    setError,
  } = useFormContext<AnalysisSearchFormType>();

  const validateCorporateNumber = useCallback(
    (values: AnalysisSearchFormType) => {
      const { analysisTarget, corporateNumber } = values;

      if (analysisTarget === 'payment' && !corporateNumber) {
        setError('corporateNumber', {
          message: t('validation.required'),
        });
        return { hasError: true };
      }

      return { hasError: false };
    },
    [t, setError],
  );

  const onSubmit = useCallback(
    async (values: AnalysisSearchFormType) => {
      const { hasError: hasProjectNumberError } = validateProjectNumber(
        values,
        setError,
      );
      const { hasError: hasCorporateNumberError } =
        validateCorporateNumber(values);
      if (hasProjectNumberError || hasCorporateNumberError) {
        return;
      }

      const { newCascadeId } = await processAnalysisCascaderComparison({
        values,
        currentCascaderValues,
        prevCascaderId: searchQueryStates.cascader,
        createQueryStore,
      });

      const requestParams = convertInputAnalysisValueToRequestParams({
        values,
        newCascadeId,
        graphType: searchQueryStates.graphType,
        order: searchQueryStates.order,
        currentFiscalYear:
          currentFiscalYear.data ?? currentFiscalYear.basicFiscalYear,
      });
      setSearchQueryStates((prev) => ({ ...prev, ...requestParams }));
      // // Note: If any field's value change then based on query params it will call api anyway, So use isDirty to avoid invalidate call
      if (!isFetching && !isDirty) {
        invalidate(searchQueryKeyMap.projectAnalysis());
      }
    },
    [
      currentCascaderValues,
      invalidate,
      isFetching,
      isDirty,
      createQueryStore,
      validateCorporateNumber,
      setSearchQueryStates,
      searchQueryStates.cascader,
      setError,
      currentFiscalYear,
      searchQueryStates.graphType,
      searchQueryStates.order,
    ],
  );

  return (
    <Form
      leavePageAlertCondition={null}
      id={FORM_ID}
      onFinish={handleSubmit(onSubmit)}
      layout="vertical"
    >
      <Flex vertical gap={16}>
        <AnalysisTargetColField />
        <Row gutter={16}>
          <FiscalYearRangeColField />
        </Row>

        <Row gutter={16}>
          <KeywordColField />
          <NestedOrganizationIdsColField />
          <PaymentColField
            resetCount={resetCount}
            onResetPayment={onResetPayment}
          />
        </Row>

        <Row gutter={16}>
          <CategoriesColField />
          <ImplementationMethodsColField />
          <NestedPolicyIdsColField />
          <AccountingTypeColField />
          <AccountingTypeIdsColField />
          <ProjectNumbersColField />
        </Row>

        <AnalysisSearchSearchFieldTags onResetPayment={onResetPayment} />
        <AdditionalSearch />

        <Row gutter={16}>
          <AggregationTypeColField />
          <AggregationTargetColField />
          <AnalysisAxisColField />
        </Row>

        <AggregationTags />
        <SearchButton formId={FORM_ID} onResetPayment={onResetPayment} />
      </Flex>
    </Form>
  );
};
